/**
 * GradientSlider: Styled range input with gradient track.
 * Used for Music Volume and SFX Volume controls.
 * Reads slider appearance settings from the store for consistent styling.
 */

import React, { useRef, useCallback, useState } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { color, font, ui } from './theme';

interface GradientSliderProps {
  label: string;
  value: number;          // 0..1
  onChange: (val: number) => void;
  gradientFrom?: string;
  gradientTo?: string;
  /** When true, lay out as [ LABEL ] [ track ] on one row (like the Controls
   *  steppers) instead of the label stacked above the track. */
  inline?: boolean;
  /** Optional readout shown at the right end of an inline slider. */
  valueText?: string;
  /** Override the inline label column width (default 46) so longer labels fit. */
  labelWidth?: number;
  /** When set, the inline readout becomes an editable field: click to type an
   *  exact value; commits on blur / Enter. The caller parses + clamps the raw
   *  string. (Only the numeric part of `valueText` is shown while editing.) */
  onCommitText?: (raw: string) => void;
}

export default function GradientSlider({
  label, value, onChange, inline = false, valueText, labelWidth, onCommitText,
}: GradientSliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const settings = useEditorStore(s => s.interfaceSettings);

  // Gradient + thumb track the ACCENT theme tokens (dim→bright), so every
  // slider re-tints with the chosen accent. Per-slider gradientFrom/To props
  // and the stored gradient colours are intentionally ignored — the design
  // system owns this now. Only sizing/track come from settings.
  const gFrom = 'var(--accent-dim)';
  const gTo = 'var(--accent)';
  // Track fill + outline match the sidebar buttons (same surface tokens), so the
  // unfilled slider reads like a button with a visible outline. Geometry still
  // comes from settings.
  const { sliderTrackHeight, sliderThumbSize } = settings;
  const sliderTrackBg = 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)';
  const sliderTrackBorderColor = 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.12)';

  const handleMouseEvent = useCallback((e: React.MouseEvent) => {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onChange(x);
  }, [onChange]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    handleMouseEvent(e);

    const onMove = (ev: MouseEvent) => {
      if (!trackRef.current) return;
      const rect = trackRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
      onChange(x);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [handleMouseEvent, onChange]);

  const pct = (value * 100).toFixed(0);

  return (
    <div style={inline
      ? { display: 'flex', alignItems: 'center', gap: 8 }
      : { marginBottom: settings.typoTitleMarginBottom + 4 }}>
      <div style={inline
        ? { ...inlineLabelStyle, ...(labelWidth ? { width: labelWidth } : null) }
        : {
            ...labelStyle,
            ...ui.subLabel,           // smaller + darker than the section title
            textAlign: 'center',
            marginBottom: settings.typoTitleMarginBottom,
          }}>{label}</div>
      <div
        ref={trackRef}
        style={{
          ...trackStyle,
          ...(inline ? { flex: 1, minWidth: 0 } : null),
          height: sliderTrackHeight,
          background: sliderTrackBg,
          borderColor: sliderTrackBorderColor,
        }}
        onMouseDown={handleMouseDown}
      >
        {/* Filled portion */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: `${pct}%`,
          background: `linear-gradient(90deg, ${gFrom}, ${gTo})`,
          borderRadius: 999,
          transition: 'width 0.05s ease',
        }} />
        {/* Thumb */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: `${pct}%`,
          transform: 'translate(-50%, -50%)',
          width: sliderThumbSize,
          height: sliderThumbSize,
          borderRadius: '50%',
          background: 'var(--bg-1)',
          boxShadow: '0 0 8px var(--accent)',
          border: '2px solid var(--accent)',
          transition: 'left 0.05s ease',
        }} />
      </div>
      {inline && valueText != null && (
        onCommitText
          ? <EditableReadout valueText={valueText} onCommit={onCommitText} />
          : <span style={inlineValueStyle}>{valueText}</span>
      )}
    </div>
  );
}

/** The right-end readout as a click-to-type field. Shows the formatted
 *  `valueText` (with units) normally; while focused it holds a plain-number
 *  draft and commits on blur / Enter (Escape cancels). */
function EditableReadout({ valueText, onCommit }: { valueText: string; onCommit: (raw: string) => void }) {
  const [draft, setDraft] = useState<string | null>(null);
  const editing = draft != null;
  return (
    <input
      // Marked so the keybinding engine lets plain keys type here but still fires
      // Ctrl/Cmd shortcuts (undo/redo) even while this readout has focus.
      data-num-readout=""
      value={editing ? draft : valueText}
      title="Click to type an exact value"
      onFocus={e => {
        // Seed the draft with just the number (strip °, ×, etc.) and select all.
        const n = parseFloat(valueText);
        setDraft(Number.isFinite(n) ? String(n) : '');
        requestAnimationFrame(() => e.target.select());
      }}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => { if (draft != null) { onCommit(draft); setDraft(null); } }}
      onKeyDown={e => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        else if (e.key === 'Escape') { setDraft(null); (e.target as HTMLInputElement).blur(); }
        else if ((e.ctrlKey || e.metaKey) && ['z', 'y'].includes(e.key.toLowerCase())) {
          // Undo/redo while editing: drop the draft so the field shows the
          // reverted value (and doesn't re-commit it on the next blur). The event
          // bubbles to the keybinding engine, which runs the actual undo/redo.
          setDraft(null);
        }
        // NOTE: no stopPropagation — Ctrl/Cmd shortcuts must reach the engine; it
        // suppresses plain typing keys for this readout (data-num-readout).
      }}
      style={editableReadoutStyle}
    />
  );
}

const containerStyle: React.CSSProperties = {
  marginBottom: 10,
};

const labelStyle: React.CSSProperties = {
  color: color.ink,
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 1.5,
  textAlign: 'center',
  marginBottom: 4,
  fontFamily: font.sans,
};

// Readout at the right end of an inline slider.
const inlineValueStyle: React.CSSProperties = {
  flexShrink: 0,
  minWidth: 34,
  textAlign: 'right',
  fontSize: 10,
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
  color: color.ink,
  fontFamily: font.sans,
};

// Editable variant of the readout — same look, but a real (borderless) input.
const editableReadoutStyle: React.CSSProperties = {
  ...inlineValueStyle,
  width: 42,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  padding: 0,
  cursor: 'text',
};

// Inline layout: fixed-width label on the left so both sliders' tracks start
// at the same x — width matches the Controls steppers' label column (46).
const inlineLabelStyle: React.CSSProperties = {
  flexShrink: 0,
  width: 46,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.5,
  color: color.ink,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontFamily: font.sans,
};

const trackStyle: React.CSSProperties = {
  position: 'relative',
  borderRadius: 999,
  cursor: 'pointer',
  border: '1px solid',
};
